import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCaptainAllTablesState } from '@/hooks/useCaptainOperationalState';
import { markAggregateDiscarded, reviveRejectedAggregate } from '@/lib/captainMesh/db';
import { syncService } from '@/lib/captainMesh/application/syncService';

export function ConflictRecoveryDialog({ runningOrder, table, meshTablesPath, addItem }) {
  const [selectedItems, setSelectedItems] = useState({});
  const [mode, setMode] = useState("prompt"); // "prompt", "review"
  const navigate = useNavigate();
  
  // runningOrder is a board row from useCaptainAllTablesState: its aggregate id
  // is `order_id`, and its lines are the conflicted capture's own fold. It has
  // no `aggregate_id` or `recovery_items` (those live on the mapping row), so
  // reading them showed "No items to recover" beside items the captain had
  // punched, and Discard threw "Invalid argument to Table.get()" on every tap,
  // leaving the table stuck on CONFLICT (C05, 2 Gardenn, 2026-10-19 20:53).
  const aggregateId = runningOrder?.order_id;
  const recoveryItems = Object.values(runningOrder?.items || {}).filter(
    i => i.status !== "cancelled" && i.status !== "removed"
  );

  // The winning order is ANOTHER live order on this table - never this capture.
  // It might take a second to sync.
  const { states } = useCaptainAllTablesState();
  const winningOrder = table
    ? states.find(s => s.table_id === table.id && s.order_id !== aggregateId && s.status !== "CONFLICTED"
        && ["open", "kot_sent", "billing"].includes(s.status)) || null
    : null;
  
  // Nobody else holds the table - not on this pad, not on the server's board.
  // Then there is nothing to merge into: the capture itself should go through.
  // Without this, "Add Selected Items" waited forever for a winning order that
  // did not exist (owner's phone, 2 Gardenn, 2026-10-19 21:57).
  // Needs the table actually loaded: an unknown status is not "free".
  const canResend = !winningOrder && !!table?.status && !["occupied", "billing"].includes(table.status);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Select all by default
    const all = {};
    recoveryItems.forEach((item, i) => { all[i] = true; });
    setSelectedItems(all);
  }, [recoveryItems.length]);
  
  if (!runningOrder || runningOrder.status !== "CONFLICTED") return null;
  
  const handleDiscard = async () => {
    try {
      await markAggregateDiscarded(aggregateId);
      navigate(meshTablesPath());
    } catch (err) {
      toast.error(`Could not discard the offline order: ${err?.message || err}`);
    }
  };
  
  const handleResend = async () => {
    setResending(true);
    try {
      await reviveRejectedAggregate(aggregateId);
      const result = await syncService.reconcileAggregate(aggregateId);
      // Success is the server naming the order - nothing less. A null result is
      // the flush declining to send at all (a capture over 12 hours old is
      // quarantined again, by design), which used to read as "reached the server".
      if (result?.conflict) {
        toast.error(`${table?.name} is held by another order on the server. Review it, or discard yours.`);
      } else if (result?.order_id) {
        toast.success(`${table?.name}: your order reached the server${result.order_number ? ` (order ${result.order_number})` : ""}`);
        navigate(meshTablesPath());
      } else if (result === null) {
        toast.error(`${table?.name}: this offline order is more than 12 hours old and was not sent. Discard it and punch the table again.`);
      } else {
        toast.error(`${table?.name}: the server did not take the order - it stays queued and will retry.`);
      }
    } catch (err) {
      // Back in the queue either way: the sync loop retries it on its own.
      toast.error(`Could not reach the server - your order is queued and will send when it is back (${err?.message || err})`);
    } finally {
      setResending(false);
    }
  };

  const handleRestoreSelected = async () => {
    if (!winningOrder) {
      // The table is held by an order this pad has no copy of (opened on the
      // till) - there is nothing here to merge into. Say what to do instead of
      // waiting forever.
      toast.error(`${table?.name} is held by an order opened elsewhere. Add your items to it from that table, then discard this one.`);
      return;
    }
    try {
      const itemsToCopy = recoveryItems.filter((_, i) => selectedItems[i]);
      for (const line of itemsToCopy) {
        await addItem({id: line.menu_item_id, name: line.item_name || line.menu_item_name}, line.variation_id ? {id: line.variation_id, name: line.variation_name} : null, line.quantity, winningOrder.order_id);
      }
      await markAggregateDiscarded(aggregateId);
      navigate(meshTablesPath());
    } catch (err) {
      toast.error(`Could not add your offline items: ${err?.message || err}`);
    }
  };
  
  if (mode === "prompt") {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in" data-testid="table-conflict-dialog">
        <div className="bg-card w-full max-w-sm rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-6">
            <h3 className="text-xl font-bold text-foreground mb-3 leading-tight">{table?.name} conflict</h3>
            <p className="text-body text-muted-foreground">
              {canResend
                ? "Your offline order did not reach the server, and no other order holds this table now."
                : "Your offline order was rejected because another order currently holds this table."}
            </p>
          </div>
          <div className="flex flex-col border-t bg-muted/10 divide-y">
            {canResend && (
              <button className="w-full text-left p-4 hover:bg-muted/30 transition-colors disabled:opacity-60" onClick={handleResend} disabled={resending} data-testid="conflict-resend">
                <div className="font-bold text-body text-primary">{resending ? "Sending…" : "Send my order"}</div>
                <div className="text-micro text-muted-foreground mt-1">Sends your {recoveryItems.length} item(s) to the server as this table's order.</div>
              </button>
            )}
            {!canResend && (
            <button className="w-full text-left p-4 hover:bg-muted/30 transition-colors" onClick={() => setMode("review")}>
              <div className="font-bold text-body text-primary">Review & Recover Items</div>
              <div className="text-micro text-muted-foreground mt-1">Selectively merge your offline items.</div>
            </button>
            )}
            <button className="w-full text-left p-4 hover:bg-muted/30 transition-colors" onClick={handleDiscard}>
              <div className="font-bold text-body text-foreground">Discard my offline order</div>
              <div className="text-micro text-muted-foreground mt-1">Leaves the winning order exactly as it is.</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // mode === "review"
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in" data-testid="conflict-recovery-dialog">
      <div className="bg-card w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden border flex flex-col max-h-[85vh]">
        <div className="p-6 border-b shrink-0 bg-muted/5">
          <h3 className="text-xl font-bold text-foreground leading-tight">Recover Items for {table?.name}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Review the winning order and select which of your offline items to add to it.
          </p>
        </div>
        
        <div className="flex-1 overflow-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Winning Order */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Existing Order</h4>
            {(!winningOrder || !winningOrder.items || Object.keys(winningOrder.items).length === 0) ? (
              <div className="text-sm text-muted-foreground italic p-4 bg-muted/10 rounded-lg">
                Waiting for order data...
              </div>
            ) : (
              <ul className="space-y-3">
                {Object.values(winningOrder.items).filter(i => i.status !== "cancelled" && i.status !== "removed").map((item, idx) => (
                  <li key={idx} className="flex justify-between items-start text-sm">
                    <span><span className="text-muted-foreground font-mono mr-2">{item.quantity}x</span> {item.item_name || item.menu_item_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Offline Snapshot */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Your Offline Items</h4>
            {recoveryItems.length === 0 ? (
              <div className="text-sm text-muted-foreground italic p-4 bg-muted/10 rounded-lg">
                No items to recover.
              </div>
            ) : (
              <ul className="space-y-2">
                {recoveryItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 p-2 hover:bg-muted/30 rounded-lg transition-colors cursor-pointer" onClick={() => setSelectedItems(prev => ({...prev, [idx]: !prev[idx]}))}>
                    <input type="checkbox" checked={!!selectedItems[idx]} readOnly className="mt-1" />
                    <div className="text-sm">
                      <div><span className="text-muted-foreground font-mono mr-2">{item.quantity}x</span> {item.item_name || item.menu_item_name}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        <div className="p-4 border-t bg-muted/5 shrink-0 flex justify-end gap-3">
          <button className="px-4 py-2 rounded-md font-semibold text-sm hover:bg-muted transition-colors" onClick={handleDiscard}>
            Discard All
          </button>
          <button className="px-4 py-2 rounded-md font-semibold text-sm bg-action text-action-foreground hover:bg-action/90 transition-colors" onClick={handleRestoreSelected} disabled={!Object.values(selectedItems).some(Boolean)}>
            Add Selected Items
          </button>
        </div>
      </div>
    </div>
  );
}
